<template>
  <!-- `persistent` : la modale ne se ferme que par ses boutons (ou Échap, qui vaut « Annuler ») —
       un clic hors de la carte ne doit pas abandonner la saisie à l'insu de l'utilisateur. -->
  <v-dialog :model-value="visible" :width="520" persistent @keydown.esc="annuler">
    <v-card class="pa-5">
      <div class="objet-titre pb-4">Objet des contrats à compléter</div>

      <div class="pb-2">
        Vous avez sélectionné <strong>{{ selectionnes }} contrat(s)</strong>.<br>
        Parmi ceux-ci, <strong>{{ sansObjet }} contrat(s)</strong> ne comporte(nt) pas d'objet.
      </div>
      <div class="pb-4">
        Souhaitez-vous appliquer un même objet à ces <strong>{{ sansObjet }} contrat(s)</strong> ?
      </div>

      <v-text-field
v-model="objet"
                    label="Objet commun *"
                    placeholder="Saisissez l'objet à appliquer"
                    hint="Cet objet sera appliqué uniquement aux contrats dont l'objet est actuellement vide."
                    persistent-hint
                    :counter="longueurMax > 0 ? longueurMax : undefined"
                    :rules="regles"
                    :disabled="enCours"
                    density="compact"
                    variant="outlined"
                    autofocus
                    @keydown.enter="valider">
      </v-text-field>

      <div class="objet-info pt-4">Les objets déjà renseignés ne seront pas modifiés.</div>

      <v-row class="ma-0 pt-5 d-flex justify-center" no-gutters>
        <div class="px-2">
          <loader-button
type="button"
                         label="Annuler"
                         :outlined="true"
                         :disabled="enCours"
                         @callback="annuler">
          </loader-button>
        </div>
        <div class="px-2">
          <loader-button
type="button"
                         label="Appliquer et continuer"
                         :disabled="!valide"
                         :loading="enCours"
                         @callback="valider">
          </loader-button>
        </div>
      </v-row>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
  import { defineComponent } from 'vue';
  import LoaderButton from '@/framework/base/components/LoaderButton.vue';

  /**
   * Saisie d'un objet commun pour les contrats sans objet d'un envoi en signature en masse (OVA-4980).
   *
   * Aucune modale existante ne saisit une valeur pour plusieurs contrats, et SectionAlert ne porte pas
   * de champ : d'où ce composant. Il est purement présentationnel — il ne connaît ni les contrats ni le
   * serveur. Le parent le pilote par `visible` et reçoit `valider(objet)` ou `annuler`.
   *
   * L'objet émis est débarrassé de ses espaces de bord : un objet fait uniquement d'espaces n'est pas
   * un objet (règle proposée au PO, à confirmer). Le serveur refait ce contrôle, sans se fier au client.
   */
  export default defineComponent({
    name: 'SectionObjetCommunDialog',
    components: {
      LoaderButton,
    },
    props: {
      visible: {
        type: Boolean,
        required: false,
      },
      /** Nombre de contrats de la sélection (X de l'US). */
      selectionnes: {
        type: Number,
        required: true,
      },
      /** Nombre de contrats sans objet (Y de l'US). */
      sansObjet: {
        type: Number,
        required: true,
      },
      /**
       * Longueur maximale de l'objet ; 0 = pas de limite. 150 par défaut, arbitrage PO du 07/10/2026 :
       * le PDF fait passer un objet long à la ligne sans difficulté, la limite ne sert qu'à le garder
       * raisonnable. Le serveur applique la même limite (OVA-5006).
       */
      longueurMax: {
        type: Number,
        required: false,
        default: 150,
      },
      /** Enregistrement en cours : champ et « Annuler » figés, roue sur « Appliquer et continuer ». */
      enCours: {
        type: Boolean,
        required: false,
        default: false,
      },
    },
    emits: ['valider', 'annuler'],
    data () {
      return {
        objet: '',
      };
    },
    computed: {
      objetNormalise (): string {
        return this.objet.trim();
      },
      trop (): boolean {
        return this.longueurMax > 0 && this.objetNormalise.length > this.longueurMax;
      },
      valide (): boolean {
        return this.objetNormalise.length > 0 && !this.trop && !this.enCours;
      },
      regles (): ((valeur: string) => true | string)[] {
        return [
          (valeur: string) => (valeur ?? '').trim().length > 0 || 'L\'objet est obligatoire.',
          (valeur: string) => this.longueurMax <= 0 || (valeur ?? '').trim().length <= this.longueurMax
            || `${this.longueurMax} caractères au maximum.`,
        ];
      },
    },
    watch: {
      // Chaque ouverture repart d'un champ vide : un objet saisi puis annulé ne doit pas réapparaître
      // pour un autre lot.
      visible (ouvert: boolean) {
        if (ouvert) {
          this.objet = '';
        }
      },
    },
    methods: {
      valider (): void {
        if (!this.valide) return;
        this.$emit('valider', this.objetNormalise);
      },
      annuler (): void {
        if (this.enCours) return;
        this.$emit('annuler');
      },
    },
  });
</script>

<style scoped>
  .objet-titre {
    font-size: 17px;
    font-weight: 600;
    color: #135253;
    text-align: center;
  }

  .objet-info {
    font-size: 14px;
    color: #5B6A71;
  }
</style>
